import { useState } from "react";
import { useOrderContext, useAppContext } from "../contexts";
import { useValidateOrder } from ".";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  updateSetting,
  setProductDetails,
  updateOtherSettings,
  updateRefundSettingsDetails,
} from "../redux/slices";
import { CURRENCY_SYMBOLS } from "../Utilities/currency";
import {
  productActionTypes,
  storeSettingTypes,
  otherSettingsActionTypes,
  refundSettingsActionTypes,
} from "../redux/utils";
import { useAPI } from "../hooks";

export const useFetchItems = () => {
  const { orderInfo, setRecentOrderID } = useOrderContext();
  const { setIsBtnDisabled, setIsInternalServerError, setIsPageLoading } =
    useAppContext();
  const [isLoader, setIsLoader] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const { formData, setFormData } = useValidateOrder();
  const settingState = useSelector((state) => state.storeSettings);
  const storeConfigState = useSelector((state) => state.storeSettings);
  const dispatch = useDispatch();
  const productDetailsState = useSelector((state) => state.productDetails);
  const navigate = useNavigate();
  const { ECORETURNS_ORDER_VALIDATION_API } = useAPI();

  const orderFetch = async (value, e, orderID, CID) => {
    setFormData({ ...formData, [e?.target?.name]: value });
    const params = new URLSearchParams(window.location.search);
    const accessCode = params.get("access_code");
    try {
      setIsLoader(true);
      setIsBtnDisabled(true);
      const response = await fetch(ECORETURNS_ORDER_VALIDATION_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_ajax: true,
          customer_email: formData.emailPhoneZipCode || CID,
          order_number: formData.orderNo || orderID,
          shop: settingState.storeUniqueID,
          access_code: accessCode,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text(); // <-- read plain text
        const error = new Error(errorText || 'Unknown error');
        error.status = response.status;
        throw error
      }

      const data = await response.json();

      const otherItemProductDetails = `${settingState.baseURL}/products`;
      dispatch(
        updateOtherSettings({
          key: otherSettingsActionTypes.UPDATE_ACCESS_CODE,
          value: accessCode,
        })
      );

      dispatch(
        setProductDetails({
          key: productActionTypes.SET_OTHER_ITEM_PRODUCT_DETAILS_URL,
          value: otherItemProductDetails,
        })
      );

      if (response.ok) {
        setIsLoader(false);
        dispatch(
          updateRefundSettingsDetails({
            key: refundSettingsActionTypes.UPDATE_ENABLE_INCENTIVE,
            value: data?.enable_incentive,
          })
        );
        dispatch(
          setProductDetails({
            key: productActionTypes.SET_CUSTOMER_ID,
            value: data?.customer_id,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.UPDATE_CUSTOMER_INFO,
            value: data?.customer_data,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_CUSTOMER_NAME,
            value: data?.customer_name,
          })
        );

        if (data?.customer_email === "") {
          dispatch(
            setProductDetails({
              key: productActionTypes.SET_CUSTOMER_CONTACT,
              value: data?.customer_contact,
            })
          );
          dispatch(
            setProductDetails({
              key: productActionTypes.SET_CUSTOMER_EMAIL,
              value: data?.customer_contact,
            })
          );
        } else {
          dispatch(
            setProductDetails({
              key: productActionTypes.SET_CUSTOMER_EMAIL,
              value: data?.customer_email,
            })
          );
        }

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_ORDER_NUMBER,
            value: data?.order_number,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_EXCHANGE_ITEM,
            value:
              productDetailsState.isPolicyOverwritten ||
              data?.enable_exchange,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_ENABLE_INCENTIVE,
            value: data?.enable_incentive,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_PAYMENT_METHOD,
            value: data?.payment_method,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.ALLOW_CUSTOMER_UPI_DETAILS,
            value: data?.allow_customer_upi_details,
          })
        );
        dispatch(
          setProductDetails({
            key: productActionTypes.ALLOW_CUSTOMER_BANK_DETAILS,
            value: data?.allow_customer_bank_details,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_TEST_MODE,
            value:
              data?.test_mode.toString().charAt(0).toUpperCase() +
              data?.test_mode.toString().slice(1),
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_RETURN_REASON,
            value: null,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_RECENT_ORDER_ID,
            value: data?.recent_order_id,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_COLLECT_PRICE_DIFFERENCE_PERCENT,
            value:
              data?.exchange_settings?.collect_price_difference_percent,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_COLLECT_PRICE_DIFFERENCE_PERCENT,
            value:
              data?.exchange_settings?.collect_price_difference_percent,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_ORIGINAL_PAYMENT_METHOD_REFUND_AVAILABLE,
            value: data?.original_payment_method_refund_avl,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_ENABLESTORE_CREDIT_REFUND,
            value: data?.enable_store_credit_refund,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_ASK_ACCOUNT_INFORMATION,
            value: data?.ask_account_information,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_CHARGE_REVERSE_SHIPMENT_EXCHANGE,
            value: data?.charge_reverse_shipment_exchange,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_REVERSE_SHIPMENT_FEE,
            value: data?.reverse_shipment_fee,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_COLLECT_PRICE_DIFFERENCE,
            value: data?.exchange_settings?.collect_price_difference,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_COLLECT_PRICE_DIFFERENCE_AMOUNT,
            value:
              data?.exchange_settings?.collect_price_difference_amount,
          })
        );

        const Currency = CURRENCY_SYMBOLS[data?.currency];

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_CURRENCY,
            value: Currency,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_SESSION_CURRENCY,
            value: data.order_data?.[0]?.currency,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_NEIGHBORHOOD_COMMERCE,
            value: data?.neighborhood_commerce,
          })
        );

        if (
          !productDetailsState.isPolicyOverwritten &&
          !data?.enable_refund &&
          !data?.enable_incentive
        ) {
          dispatch(
            setProductDetails({
              key: productActionTypes.SET_GET_REFUND,
              value: false,
            })
          );
        } else {
          dispatch(
            setProductDetails({
              key: productActionTypes.SET_GET_REFUND,
              value: true,
            })
          );
        }
        if (formData.orderNo === "0000") {
          dispatch(
            updateSetting({
              key: storeSettingTypes.UPDATE_IS_JOHN_DOE_TEST_MODE,
              value: true,
            })
          );
        } else {
          dispatch(
            updateSetting({
              key: storeSettingTypes.UPDATE_IS_JOHN_DOE_TEST_MODE,
              value: false,
            })
          );
        }

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_IS_SHOW_REFUND,
            value: data?.enable_refund,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_ORDER_INFO,
            value: data.order_data,
          })
        );

        dispatch(
          setProductDetails({
            key: productActionTypes.SET_IS_POLICY_OVERWRITTEN,
            value: data.is_policy_overwritten,
          })
        );

        setRecentOrderID(data?.recent_order_id);
        setIsBtnDisabled(false);
        navigate("/items");
      }
    } catch (error) {
      const status = error.status || 0;
      if (status === 500) {
        setIsInternalServerError(true);
        setIsLoader(false);
      } else if (status === 400) {
        setIsError(true);
        setErrorMsg(error.message);
        setIsLoader(false);
      } else {
        setIsError(true);
        setErrorMsg(storeConfigState?.themeConfig?.internal_server_error);
        setIsLoader(false);
      }
      setIsBtnDisabled(false);
    }
  };
  return {
    orderInfo,
    orderFetch,
    isLoader,
    isError,
    errorMsg,
    formData,
    setFormData,
  };
};
